import { ref, onUnmounted, getCurrentInstance } from 'vue'

// Polls our own delivery ledger (GET /v2/uber/delivery/{orderRef}/status), which
// the webhook processor keeps current — never Uber's API directly. Drives the
// ThankYou tracking block: courier name/vehicle, ETA, and a tracking_url link.
//
// Fail-soft: any error or a not-found delivery leaves status quiet so the page
// degrades to "we'll call you shortly" rather than showing an error. Polling
// stops on a terminal status or when the component unmounts.
//
// status mirrors the ledger: '' | 'pending' | 'pickup' | 'pickup_complete' |
// 'dropoff' | 'delivered' | 'canceled' | 'returned' | 'failed' | 'uber_failed'

const POLL_MS = 20000
const TERMINAL = ['delivered', 'canceled', 'returned', 'failed', 'uber_failed']

function buildUrl(root, orderRef) {
  const base = (root || '').replace(/\/$/, '')
  return `${base}/ez-clover/v2/uber/delivery/${encodeURIComponent(orderRef)}/status`
}

/**
 * @param {object} [root] _EZCL; defaults to window._EZCL
 */
export function useUberTracking(root) {
  const cfg = root || window?._EZCL || {}
  const enabled = !!cfg?.data?.delivery?.uber?.enabled

  const status = ref('')
  const trackingUrl = ref('')
  const courierName = ref('')
  const courierVehicle = ref('')
  const dropoffEta = ref('')
  const active = ref(false)

  let timer = null

  function stop() {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
    active.value = false
  }

  async function poll(orderRef) {
    try {
      const res = await fetch(buildUrl(cfg.restRoot, orderRef), {
        method: 'GET',
        headers: { 'X-WP-Nonce': cfg.nonce || '' },
      })
      const data = await res.json()
      if (data && data.ok) {
        status.value = data.status || ''
        trackingUrl.value = data.trackingUrl || ''
        courierName.value = data.courierName || ''
        courierVehicle.value = data.courierVehicle || ''
        dropoffEta.value = data.dropoffEta || ''
      }
    } catch {
      // Swallow — keep the last known state, retry on the next tick.
    }

    if (TERMINAL.includes(status.value)) {
      stop()
      return
    }
    timer = setTimeout(() => poll(orderRef), POLL_MS)
  }

  // Begin tracking an order. No-op unless Uber is enabled and we have a ref —
  // so a merchant-fulfilled or pickup order never starts polling.
  function start(orderRef) {
    if (!enabled || !orderRef) return
    stop()
    active.value = true
    poll(orderRef)
  }

  // Auto-stop with the component when used in one; harmless outside (tests).
  if (getCurrentInstance()) {
    onUnmounted(stop)
  }

  return { status, trackingUrl, courierName, courierVehicle, dropoffEta, active, start, stop }
}
